import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Avatar,
  Card,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Login as LoginIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  PersonOffOutlined as PersonOffOutlinedIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../utils/userPermissions';
import { companiesAPI as companyAPI, usersAPI as userAPI, type Company, type CompanyUser } from '../services';
import {
  getUserTypeLabel,
  getUserTypeColor
} from '../utils/userTypes';
import { getCompanyTypeLabel, getCompanyTypeColor } from '../utils/companyTypes';
import EmptyState from '../components/EmptyState';
import { HEADER_CELL_SX, StatusSummaryCards, DetailItem, type SummaryCardDef } from '../components/ListPageShared';

type StatusFilterKey = 'All' | 'active' | 'inactive';

const AVATAR_COLORS = ['#79A82A', '#2196f3', '#f44336', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#4caf50'];

const CompanyUsers: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>('All');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
  // Confirmation dialog state for the two destructive actions
  const [deleteTarget, setDeleteTarget] = useState<CompanyUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [loginAsTarget, setLoginAsTarget] = useState<CompanyUser | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Check if user is super admin using global utility
  const userIsSuperAdmin = isSuperAdmin(user);

  const fetchCompanyAndUsers = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch company and users data from API
      const [companyResponse, usersResponse] = await Promise.all([
        companyAPI.getCompanyById(companyId),
        companyAPI.getCompanyUsers(companyId)
      ]);

      setCompany(companyResponse);
      setUsers(usersResponse);
    } catch (err: any) {
      setError(t('companyUsers.fetchError'));
      console.error('Error fetching company users:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!userIsSuperAdmin) {
      setError(t('companyUsers.accessDenied'));
      setLoading(false);
      return;
    }

    if (companyId) {
      fetchCompanyAndUsers();
    }
  }, [companyId, userIsSuperAdmin, fetchCompanyAndUsers]);

  const statusCounts = useMemo(() => ({
    All: users.length,
    active: users.filter((u) => u.is_enabled).length,
    inactive: users.filter((u) => !u.is_enabled).length,
  }), [users]);

  const filteredUsers = useMemo(() => {
    const term = searchText.toLowerCase();
    return users.filter((u) => {
      const matchesStatus =
        statusFilter === 'All' || (statusFilter === 'active' ? u.is_enabled : !u.is_enabled);
      const matchesSearch =
        !term ||
        [u.username, u.full_name, u.firstname, u.lastname, u.emailaddress, u.jobtitle]
          .some((v) => v?.toLowerCase().includes(term));
      return matchesStatus && matchesSearch;
    });
  }, [users, searchText, statusFilter]);

  const paginatedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage],
  );

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredUsers.length) setPage(0);
  }, [filteredUsers.length, page, rowsPerPage]);

  const handleBack = () => {
    if (companyId) {
      navigate(`/companies/${companyId}`);
    } else {
      navigate(-1);
    }
  };

  const handleViewUser = (userId: string) => {
    navigate(`/users/${userId}/edit`);
  };

  const handleEditUser = (userId: string) => {
    navigate(`/users/${userId}/edit`);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await userAPI.deleteUser(deleteTarget.userid);
      setSnackbar({ msg: t('companyUsers.userDeleted', 'User deleted'), severity: 'success' });
      setDeleteTarget(null);
      fetchCompanyAndUsers();
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || t('companyUsers.deleteFailed', 'Failed to delete user'),
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleLoginAsConfirm = async () => {
    if (!loginAsTarget) return;
    setImpersonating(true);
    try {
      // Call API to impersonate user
      const response = await companyAPI.loginAsUser(loginAsTarget.userid);

      // Update auth context with impersonated user data
      // This will reset the current session and log in as the new user
      if (response.access_token) {
        // Store the token and user data
        localStorage.setItem('token', response.access_token);
        localStorage.setItem('user', JSON.stringify(response.user));

        // Force page reload to reinitialize the app with new user context
        window.location.href = '/';
      }
    } catch (error: any) {
      console.error('Error logging in as user:', error);
      setImpersonating(false);
      setLoginAsTarget(null);
      setSnackbar({
        msg: error?.response?.data?.detail || t('companyUsers.loginAsFailed', 'Failed to login as user. Please try again.'),
        severity: 'error',
      });
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: CompanyUser) => {
    event.stopPropagation();
    setSelectedUser(user);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const displayName = (u: CompanyUser) =>
    u.full_name || [u.firstname, u.lastname].filter(Boolean).join(' ') || u.username;

  const userAvatar = (u: CompanyUser, size = 34) => {
    const name = displayName(u);
    const initials = u.firstname && u.lastname
      ? `${u.firstname[0]}${u.lastname[0]}`
      : name[0]?.toUpperCase() || '?';
    const colorIndex = name.split('').reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length;
    return (
      <Avatar sx={{ width: size, height: size, bgcolor: AVATAR_COLORS[colorIndex], fontSize: size * 0.4 }}>
        {initials}
      </Avatar>
    );
  };

  const roleChip = (u: CompanyUser) => (
    <Chip
      label={u.user_type_name || getUserTypeLabel(u.usertype)}
      color={getUserTypeColor(u.usertype)}
      variant="outlined"
      size="small"
    />
  );

  const statusChip = (u: CompanyUser) => (
    <Chip
      label={u.is_enabled ? t('common.active') : t('common.inactive')}
      color={u.is_enabled ? 'success' : 'default'}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );

  const renderExpandedContent = (u: CompanyUser) => (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, rowGap: 2 }}>
        <DetailItem label={t('companyUsers.username', 'Username')}>@{u.username}</DetailItem>
        <DetailItem label={t('companyUsers.email', 'Email')}>{u.emailaddress || '—'}</DetailItem>
        <DetailItem label={t('companyUsers.phone', 'Phone')}>{u.mobile || u.phone || '—'}</DetailItem>
        <DetailItem label={t('companyUsers.jobTitle', 'Job Title')}>{u.jobtitle || '—'}</DetailItem>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewIcon />}
          onClick={() => handleViewUser(u.userid)}
          sx={{ textTransform: 'none' }}
        >
          {t('users.viewDetails', 'View Details')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => handleEditUser(u.userid)}
          sx={{ textTransform: 'none' }}
        >
          {t('common.edit', 'Edit')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<LoginIcon />}
          onClick={() => setLoginAsTarget(u)}
          sx={{ textTransform: 'none' }}
        >
          {t('companyUsers.loginAs', 'Login As')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteTarget(u)}
          sx={{ textTransform: 'none' }}
        >
          {t('common.delete', 'Delete')}
        </Button>
      </Box>
    </Box>
  );

  const statusCards: Array<SummaryCardDef<StatusFilterKey>> = [
    { key: 'All', label: t('users.allUsers', 'All Users'), count: statusCounts.All, icon: <GroupsIcon />, color: theme.palette.primary.main },
    { key: 'active', label: t('common.active', 'Active'), count: statusCounts.active, icon: <CheckCircleIcon />, color: theme.palette.success.main },
    { key: 'inactive', label: t('common.inactive', 'Inactive'), count: statusCounts.inactive, icon: <PersonOffOutlinedIcon />, color: theme.palette.grey[600] },
  ];

  const edgeColor = (u: CompanyUser) => (u.is_enabled ? theme.palette.success.main : theme.palette.grey[400]);

  if (!userIsSuperAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('companyUsers.accessDenied')}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          {t('companyUsers.backToCompany')}
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary =
    searchText || statusFilter !== 'All'
      ? `Showing ${filteredUsers.length} of ${users.length} users (filtered)`
      : `Total users: ${users.length}`;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            {t('companyUsers.backToCompany')}
          </Button>
          <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('companyUsers.title', 'Company Users')}
          </Typography>
        </Box>
      </Box>

      {/* Company Info */}
      {company && (
        <Box mb={3}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">
                {company.companyname}
              </Typography>
              <Chip
                label={getCompanyTypeLabel(t, company.companytype)}
                color={getCompanyTypeColor(company.companytype)}
                size="small"
              />
              <Chip
                label={company.isactive ? t('common.active') : t('common.inactive')}
                color={company.isactive ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Status summary cards — act as the status filter */}
      <StatusSummaryCards
        cards={statusCards}
        selected={statusFilter}
        allKey="All"
        onSelect={(key) => {
          setStatusFilter(key);
          setPage(0);
        }}
      />

      {/* Filter bar */}
      <Box mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          variant="outlined"
          placeholder={t('companyUsers.searchPlaceholder', 'Search by name, username, email or job title')}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0);
          }}
          sx={{ flex: 1, minWidth: 200, maxWidth: { xs: '100%', sm: 400 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={t('common.refresh')}>
          <span>
            <IconButton onClick={fetchCompanyAndUsers} disabled={loading} aria-label={t('common.refresh')}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Mobile card list */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {paginatedUsers.length === 0 ? (
            <Paper sx={{ p: 3 }}>
              <EmptyState
                title={t('users.noUsers', 'No users found')}
                description={t('users.noUsersDescription', 'Try adjusting your filters')}
                icon={<SearchIcon />}
              />
            </Paper>
          ) : (
            paginatedUsers.map((u) => {
              const expanded = expandedId === u.userid;
              return (
                <Card key={u.userid} variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${edgeColor(u)}` }}>
                  <Box
                    onClick={() => setExpandedId(expanded ? null : u.userid)}
                    sx={{ px: 2, py: 1.5, cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {userAvatar(u, 38)}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {displayName(u)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          @{u.username}
                        </Typography>
                      </Box>
                      {statusChip(u)}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                      {roleChip(u)}
                    </Box>
                  </Box>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                      {renderExpandedContent(u)}
                    </Box>
                  </Collapse>
                </Card>
              );
            })
          )}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table with expandable rows */
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 40, p: 0.5 }} />
                <TableCell sx={HEADER_CELL_SX}>{t('users.fullName', 'User')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('companyUsers.email', 'Email')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('companyUsers.role', 'Role')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('companyUsers.jobTitle', 'Job Title')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <EmptyState
                      title={t('users.noUsers', 'No users found')}
                      description={t('users.noUsersDescription', 'Try adjusting your filters')}
                      icon={<SearchIcon />}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((u) => {
                  const expanded = expandedId === u.userid;
                  return (
                    <React.Fragment key={u.userid}>
                      <TableRow
                        hover
                        onClick={() => setExpandedId(expanded ? null : u.userid)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: expanded ? 'action.selected' : undefined,
                          '& > td:first-of-type': { borderLeft: `4px solid ${edgeColor(u)}` },
                        }}
                      >
                        <TableCell sx={{ p: 0.5 }} align="center">
                          <IconButton size="small" aria-label={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}>
                            {expanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            {userAvatar(u)}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {displayName(u)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap display="block">
                                @{u.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ color: u.emailaddress ? 'text.primary' : 'text.disabled' }}>
                            {u.emailaddress || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{roleChip(u)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ color: u.jobtitle ? 'text.primary' : 'text.disabled' }}>
                            {u.jobtitle || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{statusChip(u)}</TableCell>
                        <TableCell align="right" sx={{ pr: 1 }}>
                          <IconButton size="small" onClick={(e) => handleMenuClick(e, u)} aria-label={t('common.actions', 'Actions')}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 3, py: 2, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
                              {renderExpandedContent(u)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
              {statusFilter !== 'All' && (
                <span> &bull; {t('common.status')}: {statusFilter === 'active' ? t('common.active') : t('common.inactive')}</span>
              )}
            </Typography>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('common.rowsPerPage')}
            />
          </Box>
        </TableContainer>
      )}

      {/* More Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {selectedUser && (
          <MenuItem onClick={() => {
            handleViewUser(selectedUser.userid);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('users.viewDetails', 'View Details')}</ListItemText>
          </MenuItem>
        )}
        {selectedUser && (
          <MenuItem onClick={() => {
            handleEditUser(selectedUser.userid);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit')}</ListItemText>
          </MenuItem>
        )}
        {selectedUser && (
          <MenuItem onClick={() => {
            setLoginAsTarget(selectedUser);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <LoginIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('companyUsers.loginAs')}</ListItemText>
          </MenuItem>
        )}
        {selectedUser && (
          <MenuItem onClick={() => {
            setDeleteTarget(selectedUser);
            handleMenuClose();
          }} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Login-as confirmation. This ends the super admin session, so it gets
          a real dialog rather than window.confirm. */}
      <Dialog
        open={loginAsTarget !== null}
        onClose={() => !impersonating && setLoginAsTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={impersonating}
      >
        <DialogTitle>
          {t('companyUsers.loginAsConfirmTitle', { defaultValue: 'Login as {{name}}?', name: loginAsTarget?.username })}
        </DialogTitle>
        <DialogContent>
          {loginAsTarget && !loginAsTarget.is_enabled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('companyUsers.loginAsInactiveWarning', { defaultValue: '"{{name}}" is currently inactive.', name: loginAsTarget.username })}
            </Alert>
          )}
          <DialogContentText>
            {t('companyUsers.loginAsConfirmBody', {
              defaultValue:
                'Your super admin session will end and you will be logged in as "{{name}}". Log out and sign back in to return to your own account.',
              name: loginAsTarget?.username,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginAsTarget(null)} disabled={impersonating}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleLoginAsConfirm}
            disabled={impersonating}
            startIcon={impersonating ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
          >
            {impersonating
              ? t('companyUsers.loggingInAs', 'Logging in...')
              : t('companyUsers.loginAs', 'Login As')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete-user confirmation */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>{t('companyUsers.deleteConfirmTitle', 'Delete user?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('companyUsers.deleteConfirmBody', {
              defaultValue: 'Delete user "{{name}}"? They will no longer be able to sign in.',
              name: deleteTarget?.username,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('companyUsers.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyUsers;
